import { useCallback, useMemo, useState } from 'react';
import { IconeDupliquer, IconePlus, paveTeinte, useTheme } from '@journal/ui';
import type { Creneau } from '../pages/EmploiDuTemps.tsx';
import { JOURS } from '../pages/EmploiDuTemps.tsx';

/**
 * Emploi du temps sur axe horaire.
 *
 * Restaure `renderGrid` de l'application d'origine. Les cinq listes empilées qui
 * tenaient ce rôle disaient bien ce qu'il y avait dans la journée, mais pas
 * QUAND : deux créneaux de durées très différentes s'y affichaient à l'identique,
 * et un trou d'une heure entre midi et deux ne se voyait pas.
 *
 * ── Position et hauteur portent le temps ────────────────────────────────────
 * Un créneau est placé à son heure de début et haut de sa durée. C'est ce qui
 * fait qu'on repère d'un regard une matinée surchargée, un trou, ou deux
 * créneaux qui se chevauchent — sans lire une seule heure.
 *
 * ── Le glisser-déposer n'est PAS le seul chemin ─────────────────────────────
 * Déplacer un créneau à la souris est commode mais inaccessible au clavier et
 * pénible au doigt. Le dialogue « Modifier » permet la même chose en changeant
 * le jour, et reste le chemin de référence. Le glisser vient en plus.
 */

/** Bornes de l'axe, en minutes depuis minuit. Reprises de l'original. */
const DEBUT_JOURNEE = 7 * 60;
const FIN_JOURNEE = 19 * 60;
/** Pixels par minute : 1,4 donne une heure de 84 px, lisible sans être haute. */
const PIXELS_PAR_MINUTE = 1.4;

const enMinutes = (heure: string): number => {
  const [h, m] = heure.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Décalage vertical d'un créneau, en pixels depuis le haut de la grille. */
export const positionDe = (debut: string): number =>
  Math.max(0, (enMinutes(debut) - DEBUT_JOURNEE) * PIXELS_PAR_MINUTE);

/**
 * Hauteur d'un créneau.
 *
 * Plancher à 22 px : un créneau de quinze minutes serait sinon trop bas pour
 * afficher son intitulé, et deviendrait un trait de couleur illisible.
 */
export const hauteurDe = (debut: string, fin: string): number =>
  Math.max(22, (enMinutes(fin) - enMinutes(debut)) * PIXELS_PAR_MINUTE);

/** « 08:30 » → « 8h30 », « 09:00 » → « 9h ». */
export const heureCourte = (heure: string): string =>
  heure.replace(/^0/, '').replace(':00', 'h').replace(':', 'h');

export function GrilleHoraire({
  creneaux, conflits, surOuverture, surDuplication, surDeplacement, surCreation,
}: {
  creneaux: Creneau[];
  conflits: Set<string>;
  surOuverture: (creneau: Creneau) => void;
  surDuplication: (creneau: Creneau) => void;
  /** Déplace un créneau vers un autre jour. */
  surDeplacement: (creneau: Creneau, jour: number) => void;
  /** Crée un créneau. `debut` est `null` depuis le bouton d'en-tête. */
  surCreation: (jour: number, debut: string | null) => void;
}) {
  const [survole, setSurvole] = useState<number | null>(null);
  const [glisse, setGlisse] = useState<string | null>(null);
  const { theme } = useTheme();

  /*
   * Le pavé d'une couleur de matière, dans le thème en cours.
   *
   * La couleur vient de la base : c'est l'enseignant qui la saisit, et aucune
   * relecture ne peut donc garantir qu'elle sera lisible. La dérivation le
   * garantit, elle, pour n'importe quelle valeur — y compris un noir presque
   * pur ou un jaune presque blanc.
   */
  const pave = useCallback(
    (couleur: string) => paveTeinte(couleur, theme),
    [theme],
  );

  /** Graduations toutes les trente minutes ; l'heure pleine est plus marquée. */
  const graduations = useMemo(() => {
    const lignes = [];
    for (let m = DEBUT_JOURNEE; m <= FIN_JOURNEE; m += 30) {
      lignes.push({
        minutes: m,
        y: (m - DEBUT_JOURNEE) * PIXELS_PAR_MINUTE,
        pleine: m % 60 === 0,
        libelle: `${String(Math.floor(m / 60)).padStart(2, '0')}h${
          m % 60 === 0 ? '' : String(m % 60).padStart(2, '0')}`,
      });
    }
    return lignes;
  }, []);

  const hauteur = (FIN_JOURNEE - DEBUT_JOURNEE) * PIXELS_PAR_MINUTE;

  return (
    <div
      data-impression="emploi-du-temps"
      /*
       * `pb-2` : de la place pour la MOITIÉ BASSE du dernier libellé d'heure.
       *
       * « 19h » est centré sur la dernière graduation, donc à cheval sur le bas
       * du planning : il dépassait de 5 px. Sur un cadre en `overflow-x`, la
       * règle veut que l'autre axe passe lui aussi en défilement — d'où une
       * barre verticale permanente, à côté de celle de la page, pour cinq
       * pixels de libellé.
       */
      className="impression-paysage overflow-x-auto pb-2"
    >
      <div className="flex min-w-[46rem] gap-1">
        {/* Colonne des heures, hors des jours : elle ne défile pas avec eux et
            reste lisible quel que soit le nombre de colonnes. */}
        <div className="w-11 shrink-0" aria-hidden="true">
          <div className="h-7" />
          <div className="relative" style={{ height: hauteur }}>
            {graduations.map((g) => (
              <span
                key={g.minutes}
                className={[
                  'absolute right-1 -translate-y-1/2 text-[10px] leading-none',
                  g.pleine ? 'text-[var(--color-fg)]' : 'text-[var(--color-fg-muted)]',
                ].join(' ')}
                style={{ top: g.y }}
              >
                {g.libelle}
              </span>
            ))}
          </div>
        </div>

        {JOURS.map((jour) => {
          const duJour = creneaux
            .filter((c) => c.weekday === jour.numero)
            .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

          return (
            <section key={jour.numero} className="min-w-0 flex-1">
              <h3 className="flex h-7 items-center justify-center gap-1 rounded-t-md bg-[var(--color-surface-raised)] text-[12px] font-semibold">
                {jour.nom}
                <button
                  type="button"
                  onClick={() => surCreation(jour.numero, null)}
                  aria-label={`Ajouter un créneau le ${jour.nom.toLowerCase()}`}
                  className="rounded p-0.5 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                >
                  <IconePlus taille={13} />
                </button>
              </h3>

              {/*
                * La zone de dépôt est la colonne entière : viser un interstice
                * entre deux créneaux serait un jeu d'adresse. Le créneau
                * conserve son heure, seul le jour change — c'est ce que fait
                * l'original, et c'est ce qu'on attend en tirant vers la droite.
                */}
              <div
                /*
                 * Clic sur une plage libre : crée un créneau à l'heure visée,
                 * arrondie au quart d'heure. C'est le geste attendu d'une
                 * grille horaire — désigner un moment plutôt que le ressaisir
                 * dans un formulaire.
                 *
                 * Le bouton « + » de l'en-tête fait la même chose sans heure :
                 * il reste le chemin accessible au clavier, où l'on ne peut
                 * pas viser une ordonnée.
                 */
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  /*
                   * ── Deux unités qu'il ne faut pas confondre ──────────────
                   *
                   * `clientY` et `getBoundingClientRect()` rendent des pixels
                   * TELS QU'AFFICHÉS. L'application applique un `zoom` CSS sur
                   * son contenu (voir `Coquille`), si bien qu'à 160 % un point
                   * situé à 420 px dans la mise en page en mesure 672 à
                   * l'écran. `PIXELS_PAR_MINUTE`, lui, est une constante de
                   * mise en page.
                   *
                   * Diviser l'un par l'autre sans corriger l'échelle décalait
                   * l'heure du facteur de zoom exactement : un clic sur midi
                   * ouvrait la boîte sur quinze heures. Le défaut était
                   * invisible à 100 %, donc invisible en développement.
                   *
                   * L'échelle se déduit de l'élément lui-même — sa hauteur
                   * affichée rapportée à celle qu'on lui a donnée — plutôt que
                   * de relire la variable CSS : une seule source, et elle
                   * suit toute mise à l'échelle, d'où qu'elle vienne.
                   */
                  const rect = e.currentTarget.getBoundingClientRect();
                  const echelle = rect.height > 0 ? rect.height / hauteur : 1;
                  const y = (e.clientY - rect.top) / echelle;
                  const minutes = DEBUT_JOURNEE + Math.round(y / PIXELS_PAR_MINUTE / 15) * 15;
                  const borne = Math.min(Math.max(minutes, DEBUT_JOURNEE), FIN_JOURNEE - 30);
                  surCreation(
                    jour.numero,
                    `${String(Math.floor(borne / 60)).padStart(2, '0')}:${
                      String(borne % 60).padStart(2, '0')}`,
                  );
                }}
                onDragOver={(e) => { e.preventDefault(); setSurvole(jour.numero); }}
                onDragLeave={() => setSurvole((j) => (j === jour.numero ? null : j))}
                onDrop={(e) => {
                  e.preventDefault();
                  setSurvole(null);
                  const creneau = creneaux.find((c) => c.id === glisse);
                  setGlisse(null);
                  if (creneau && creneau.weekday !== jour.numero) {
                    surDeplacement(creneau, jour.numero);
                  }
                }}
                className={[
                  'relative border-l border-[var(--color-border)] transition-colors duration-100',
                  'motion-reduce:transition-none',
                  survole === jour.numero ? 'bg-[var(--color-primary-soft)]' : '',
                ].join(' ')}
                style={{ height: hauteur }}
              >
                {graduations.map((g) => (
                  <span
                    key={g.minutes}
                    aria-hidden="true"
                    className={[
                      'pointer-events-none absolute inset-x-0 border-t',
                      g.pleine
                        ? 'border-[var(--color-border-strong)] opacity-40'
                        : 'border-dashed border-[var(--color-border)]',
                    ].join(' ')}
                    style={{ top: g.y }}
                  />
                ))}

                {duJour.map((creneau) => {
                  const h = hauteurDe(creneau.startsAt, creneau.endsAt);
                  const compact = h < 40;
                  const enConflit = conflits.has(creneau.id);
                  return (
                    <div
                      key={creneau.id}
                      draggable
                      onDragStart={() => setGlisse(creneau.id)}
                      onDragEnd={() => { setGlisse(null); setSurvole(null); }}
                      /*
                       * ── Pavé TEINTÉ, et non pavé plein ────────────────────
                       * Le créneau portait la couleur de la matière en aplat,
                       * surmontée d'un `text-white` écrit à la main. Mesuré :
                       * 3,15:1 sur le bleu de « Français », 2,19:1 sur l'orange
                       * d'« Éducation physique » — sous le seuil de lisibilité,
                       * et pire encore une fois imprimé, le navigateur écartant
                       * les fonds par défaut.
                       *
                       * La couleur passe donc au fond dilué et au filet plein,
                       * le texte à une encre dérivée : la teinte identifie
                       * toujours la matière d'un coup d'œil, et le libellé se
                       * lit. Voir `paveTeinte`.
                       */
                      style={{
                        top: positionDe(creneau.startsAt),
                        height: h,
                        backgroundColor: pave(creneau.color).fond,
                        color: pave(creneau.color).encre,
                        borderLeft: `3px solid ${creneau.color}`,
                      }}
                      className={[
                        'absolute inset-x-0.5 overflow-hidden rounded-md',
                        'shadow-[var(--ombre-carte)]',
                        glisse === creneau.id ? 'opacity-40' : '',
                        // Le conflit est signalé par un contour ET par du texte
                        // dans le libellé accessible : la couleur seule
                        // n'informe ni un daltonien ni un lecteur d'écran.
                        enConflit ? 'ring-2 ring-inset ring-[var(--color-danger)]' : '',
                      ].join(' ')}
                    >
                      <button
                        type="button"
                        onClick={() => surOuverture(creneau)}
                        aria-label={
                          `${jour.nom} ${heureCourte(creneau.startsAt)} à `
                          + `${heureCourte(creneau.endsAt)} · `
                          + `${creneau.subjectName || creneau.label || 'sans matière'}`
                          + `${enConflit ? ', chevauche un autre créneau' : ''}`
                          + ', modifier'
                        }
                        // Le libellé entier au survol : un nom de matière long
                        // finit tronqué dans une colonne étroite.
                        title={
                          `${heureCourte(creneau.startsAt)}–${heureCourte(creneau.endsAt)} · `
                          + `${creneau.subjectName || creneau.label || 'sans matière'}`
                          // Sans matière, le serveur rend l'intitulé comme nom :
                          // ne pas l'écrire deux fois.
                          + `${creneau.label && creneau.subjectName && creneau.label !== creneau.subjectName
                            ? ` · ${creneau.label}` : ''}`
                        }
                        className={[
                          /*
                           * `imprimable` : ce bouton n'est pas une commande
                           * posée à côté du contenu, il EST le créneau. La
                           * préparation du document retire les boutons — sans
                           * cette marque, le planning s'imprimait avec ses
                           * heures et ses jours, mais vide de ses vingt-cinq
                           * créneaux.
                           */
                          'imprimable',
                          'block h-full w-full cursor-pointer text-left',
                          compact ? 'px-1.5 py-0' : 'px-2 py-1',
                          'transition-[filter] duration-100 hover:brightness-110',
                          'focus-visible:outline-2 focus-visible:-outline-offset-2',
                          'focus-visible:outline-white motion-reduce:transition-none',
                        ].join(' ')}
                      >
                        {compact ? (
                          /*
                           * ── Une seule ligne quand le créneau est court ────
                           * Un quart d'heure fait 22 px : l'horaire PUIS la
                           * matière, sur deux lignes, n'y tenaient pas, et le
                           * bouton centrant son contenu, les deux étaient
                           * rognées — « Enseignements artistiques » coupé à
                           * mi-hauteur. L'heure de début suffit ici (la fin se
                           * lit sur la grille) et le libellé entier reste dans
                           * l'infobulle.
                           */
                          <span className="flex min-w-0 items-baseline gap-1 leading-tight">
                            <span className="shrink-0 text-[9px] opacity-80">
                              {heureCourte(creneau.startsAt)}
                            </span>
                            <span className="min-w-0 truncate text-[11px] font-semibold">
                              {creneau.subjectName || creneau.label || '–'}
                            </span>
                          </span>
                        ) : (
                          <>
                            <span className="block text-[9px] leading-tight opacity-80">
                              {`${heureCourte(creneau.startsAt)}–${heureCourte(creneau.endsAt)}`}
                            </span>
                            <span className="block truncate text-[11px] font-semibold leading-tight">
                              {creneau.subjectName || creneau.label || '–'}
                            </span>
                          </>
                        )}
                        {!compact && creneau.label && creneau.subjectName
                          && creneau.label !== creneau.subjectName && (
                          <span className="block truncate text-[9px] leading-tight opacity-80">
                            {creneau.label}
                          </span>
                        )}
                      </button>

                      {!compact && (
                        <button
                          type="button"
                          onClick={() => surDuplication(creneau)}
                          aria-label={
                            `Dupliquer le créneau de ${jour.nom} `
                            + `${heureCourte(creneau.startsAt)}`
                          }
                          /*
                           * Le blanc translucide supposait un pavé plein et
                           * sombre. Sur un fond teinté clair, il devenait un
                           * bouton blanc sur blanc. La commande emprunte donc
                           * l'encre du créneau, qui est déjà calculée pour se
                           * lire sur ce fond-là.
                           */
                          style={{ color: pave(creneau.color).encre }}
                          className="absolute right-0.5 top-0.5 rounded p-0.5 opacity-70 hover:bg-[var(--color-surface-hover)] hover:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                        >
                          <IconeDupliquer taille={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
